/**
 * COMMERCE-WORKSPACE-FRAMEWORK-000001 — workspace composition shell.
 * Hosts WorkspaceHeader, connection chips, peer nav, summary, legend, actions.
 */
import type { ReactNode } from 'react';
import { WorkspaceHeader, ActionBar } from '../shell';
import { CommercePeerNav } from './CommercePeerNav';
import type { CommerceWorkspaceShellProps } from './types';

const chip =
  'rounded-md border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-2 py-1 text-[11px] sm:text-xs text-[var(--shell-fg-muted)]';

export function CommerceWorkspace(props: CommerceWorkspaceShellProps) {
  const {
    domain,
    title,
    subtitle,
    entryKey,
    connection,
    domainBadge,
    children,
    legend,
    summary,
    primaryActions,
    secondaryActions,
    quickFilters,
    actionBar,
    peers,
  } = props;

  const seller =
    connection?.seller_ref ||
    connection?.metadata?.selling_partner_id ||
    (connection?.id ? connection.id.slice(0, 8) : '—');
  const mps =
    connection?.marketplace_ids?.join(', ') ||
    connection?.marketplaces?.map((m) => m.marketplace_id).join(', ') ||
    '—';

  return (
    <div className="commerce-workspace mx-auto max-w-[100rem] space-y-4">
      <WorkspaceHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={[
          { label: 'Founder OS', path: '/command' },
          { label: 'Commerce' },
          { label: title },
        ]}
        primaryActions={primaryActions}
        secondaryActions={
          <>
            {secondaryActions}
            <div className="flex flex-col items-end gap-1 text-right text-xs text-[var(--shell-fg-muted)]">
              <span>
                Entry ·{' '}
                <span className="font-mono font-medium text-[var(--shell-fg)]">{entryKey}</span>
              </span>
              {domainBadge ? (
                <span className="rounded-full bg-[var(--shell-accent-muted)] px-2 py-0.5 font-medium text-[var(--shell-accent)]">
                  {domainBadge}
                </span>
              ) : null}
            </div>
          </>
        }
        quickFilters={quickFilters}
      />

      <div className="flex flex-wrap gap-2">
        <span className={chip}>
          Seller <span className="ml-1 font-mono font-medium text-[var(--shell-fg)]">{seller}</span>
        </span>
        <span className={chip}>
          Marketplace{' '}
          <span className="ml-1 font-mono font-medium text-[var(--shell-fg)]">{mps}</span>
        </span>
        <span className={chip}>
          Connection{' '}
          <span className="ml-1 font-mono font-medium text-[var(--shell-fg)]">
            {connection?.id?.slice(0, 8) ?? '—'}
          </span>
        </span>
      </div>

      <CommercePeerNav domain={domain} peers={peers} />

      {summary}
      {legend}
      {actionBar ? <ActionBar>{actionBar}</ActionBar> : null}
      {children}
    </div>
  );
}

/** @deprecated Prefer CommerceWorkspace — alias for cert-era FounderCommerceShell */
export function FounderCommerceShell(props: CommerceWorkspaceShellProps): ReactNode {
  return <CommerceWorkspace {...props} />;
}
